/**
 * An offline sale, worked out on the tablet: what each dish costs from the
 * menu it last saw, the total, the kitchen's ticket, the guest's receipt, and
 * what is sent to the server when the line comes back.
 *
 * Money is done in whole minor units (pence, cents) and turned back into a
 * decimal string only at the edges, so 0.1 + 0.2 never reaches a receipt. The
 * server records the sale at exactly these prices (`fiscal.offline`): what is
 * printed here is what is kept.
 */

import type { MenuItem, OfflineSale, OfflineSalePayment } from '@restaurant/api-client';
import type { PrintLine } from '@restaurant/ui';

export interface CartLine {
  key: string;
  item: MenuItem;
  variantId: string | null;
  optionIds: string[];
  qty: number;
}

/** Minor units from a decimal string: "12.50" → 1250. */
export function cents(amount: string): number {
  const [whole = '0', frac = ''] = amount.trim().replace('-', '').split('.');
  const value = Number(whole) * 100 + Number((frac + '00').slice(0, 2));
  return amount.trim().startsWith('-') ? -value : value;
}

/** A decimal string from minor units: 1250 → "12.50". */
export function decimal(value: number): string {
  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);
  return `${sign}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
}

/** One of this dish, with its size and options, as the till charges it. */
export function unitCents(line: Pick<CartLine, 'item' | 'variantId' | 'optionIds'>): number {
  const variant = line.item.variants.find((v) => v.id === line.variantId);
  const options = line.item.modifier_groups
    .flatMap((group) => group.options)
    .filter((option) => line.optionIds.includes(option.id));
  return (
    cents(line.item.price) +
    (variant ? cents(variant.price_delta) : 0) +
    options.reduce((sum, option) => sum + cents(option.price_delta), 0)
  );
}

export function lineCents(line: CartLine): number {
  return unitCents(line) * line.qty;
}

export function totalCents(lines: CartLine[]): number {
  return lines.reduce((sum, line) => sum + lineCents(line), 0);
}

export function describeLine(line: CartLine): {
  name: string;
  variant: string | null;
  options: string[];
} {
  const variant = line.item.variants.find((v) => v.id === line.variantId)?.name ?? null;
  const options = line.item.modifier_groups
    .flatMap((group) => group.options)
    .filter((option) => line.optionIds.includes(option.id))
    .map((option) => option.name);
  return { name: line.item.name, variant, options };
}

/** The kitchen's ticket: big enough to read from the pass. */
export function kitchenTicket(
  lines: CartLine[],
  { table, at, width }: { table: string | null; at: Date; width: number },
): PrintLine[] {
  const out: PrintLine[] = [
    { text: table ? `Table ${table}` : 'Counter', align: 'center', big: true, bold: true },
    {
      text: `Offline · ${at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`,
      align: 'center',
    },
    { text: '-'.repeat(width) },
  ];
  for (const line of lines) {
    const { name, variant, options } = describeLine(line);
    out.push({
      text: `${line.qty} x ${name}${variant ? ` (${variant})` : ''}`,
      big: true,
      bold: true,
    });
    for (const option of options) out.push({ text: `    + ${option}` });
  }
  out.push({ text: '-'.repeat(width) });
  return out;
}

export interface PaymentTaken {
  tender: 'cash' | 'card';
  /** What came off the bill. */
  amountCents: number;
  /** What changed hands, for cash: the note given. */
  tenderedCents: number;
  tipCents: number;
  at: string;
  cardReaderId: string | null;
  authCode: string | null;
  cardLast4: string | null;
}

/** The receipt as the shared receipt reader expects it, from the sale alone. */
export function receiptPayload(
  lines: CartLine[],
  {
    locationName,
    table,
    guests,
    payment,
    closedAt,
  }: {
    locationName: string;
    table: string | null;
    guests: number;
    payment: PaymentTaken;
    closedAt: string;
  },
): Record<string, unknown> {
  const total = decimal(totalCents(lines));
  const change = payment.tenderedCents - payment.amountCents;
  return {
    location: { name: locationName },
    table,
    guests,
    lines: lines.map((line) => {
      const { name, variant } = describeLine(line);
      const options = line.item.modifier_groups
        .flatMap((group) => group.options)
        .filter((option) => line.optionIds.includes(option.id));
      return {
        name,
        variant,
        qty: String(line.qty),
        unit_price: decimal(unitCents(line)),
        line_total: decimal(lineCents(line)),
        modifiers: options.map((o) => ({ name: o.name, price: decimal(cents(o.price_delta)) })),
      };
    }),
    subtotal: total,
    total,
    // Worked out by the server when the sale is sent; the paper says so.
    tax_total: null,
    payments: [
      {
        tender_type: payment.tender,
        amount: decimal(payment.amountCents),
        tendered: decimal(payment.tenderedCents),
        change: decimal(Math.max(change, 0)),
        tip: decimal(payment.tipCents),
      },
    ],
    tips: decimal(payment.tipCents),
    closed_at: closedAt,
    offline: true,
  };
}

/** What is sent to the server for this sale when the line comes back. */
export function saleForServer(
  lines: CartLine[],
  {
    clientId,
    locationId,
    terminalId,
    openedAt,
    closedAt,
    receiptNumber,
    tableId,
    guests,
    payment,
  }: {
    clientId: string;
    locationId: string;
    terminalId: string;
    openedAt: string;
    closedAt: string;
    receiptNumber: number;
    tableId: string | null;
    guests: number;
    payment: PaymentTaken;
  },
): OfflineSale {
  const paid: OfflineSalePayment = {
    tender_type: payment.tender,
    amount: decimal(payment.amountCents),
    tip: decimal(payment.tipCents),
    at: payment.at,
    ...(payment.tender === 'cash' && payment.tenderedCents > payment.amountCents
      ? { tendered: decimal(payment.tenderedCents) }
      : {}),
    ...(payment.cardReaderId ? { card_reader_id: payment.cardReaderId } : {}),
    ...(payment.authCode ? { auth_code: payment.authCode } : {}),
    ...(payment.cardLast4 ? { card_last4: payment.cardLast4 } : {}),
  };
  return {
    client_id: clientId,
    location_id: locationId,
    terminal_id: terminalId,
    opened_at: openedAt,
    closed_at: closedAt,
    receipt_number: receiptNumber,
    table_id: tableId,
    guest_count: guests,
    lines: lines.map((line) => ({
      item_id: line.item.id,
      variant_id: line.variantId,
      modifier_option_ids: line.optionIds,
      qty: String(line.qty),
      unit_price: decimal(unitCents(line)),
    })),
    payments: [paid],
  };
}
